/**
 * tech-stack.tsx
 *
 * Strongly typed registry of technologies + their display icons.
 * 
 * Each entry provides:
 * - `name`: human readable title
 * - `Icon`: A React component or `null` (if no icon available)
 *
 * Icons come from:
 * - @react-icons/all-files (tree‑shakable)
 * - Custom SVG imports from /public/icons/tech-logos/
 *
 * Fully compatible with:
 * - React 18
 * - Next.js (SVG components)
 * - TypeScript strict mode
 */

import { FC } from "react";

/* -------------------------------------------------------------------------- */
/*                               Types                                         */
/* -------------------------------------------------------------------------- */

export type TechIconComponent = FC<{ className?: string }> | null;

export interface TechItem {
  name: string;
  Icon: TechIconComponent;
}

export type TechRegistry = Record<string, TechItem>;

/**
 * Wrap a react-icon or SVG component into a consistent icon renderer.
 */
const wrap =
  (Icon: FC<{ className?: string }> | any): TechIconComponent =>
    ({ className }) =>
      <Icon className={className} />;

/* ---------------------------- React-Icons Imports ------------------------ */
/* (Your imports remain EXACTLY the same — they are correct & tree-shakable) */

import { AiOutlineConsoleSql } from "@react-icons/all-files/ai/AiOutlineConsoleSql";
import { FaAws } from "@react-icons/all-files/fa/FaAws";
import { ImUpload } from "@react-icons/all-files/im/ImUpload";
import { IoLogoSass } from "@react-icons/all-files/io5/IoLogoSass";
import { IoLogoVercel } from "@react-icons/all-files/io5/IoLogoVercel";
import { MdDesktopMac } from "@react-icons/all-files/md/MdDesktopMac";
import { SiAdobeillustrator } from "@react-icons/all-files/si/SiAdobeillustrator";
import { SiAdobephotoshop } from "@react-icons/all-files/si/SiAdobephotoshop";
import { SiCss3 } from "@react-icons/all-files/si/SiCss3";
import { SiEslint } from "@react-icons/all-files/si/SiEslint";
import { SiFacebook } from "@react-icons/all-files/si/SiFacebook";
import { SiFigma } from "@react-icons/all-files/si/SiFigma";
import { SiFirebase } from "@react-icons/all-files/si/SiFirebase";
import { SiFramer } from "@react-icons/all-files/si/SiFramer";
import { SiGit } from "@react-icons/all-files/si/SiGit";
import { SiGithub } from "@react-icons/all-files/si/SiGithub";
import { SiGooglechrome } from "@react-icons/all-files/si/SiGooglechrome";
import { SiGraphql } from "@react-icons/all-files/si/SiGraphql";
import { SiHtml5 } from "@react-icons/all-files/si/SiHtml5";
import { SiInstagram } from "@react-icons/all-files/si/SiInstagram";
import { SiIntellijidea } from "@react-icons/all-files/si/SiIntellijidea";
import { SiIonic } from "@react-icons/all-files/si/SiIonic";
import { SiJavascript } from "@react-icons/all-files/si/SiJavascript";
import { SiJest } from "@react-icons/all-files/si/SiJest";
import { SiMarkdown } from "@react-icons/all-files/si/SiMarkdown";
import { SiMicrosoftoffice } from "@react-icons/all-files/si/SiMicrosoftoffice";
import { SiMicrosoftteams } from "@react-icons/all-files/si/SiMicrosoftteams";
import { SiMysql } from "@react-icons/all-files/si/SiMysql";
import { SiNetlify } from "@react-icons/all-files/si/SiNetlify";
import { SiNextDotJs } from "@react-icons/all-files/si/SiNextDotJs";
import { SiRust } from "@react-icons/all-files/si/SiRust";
import { SiNodeDotJs } from "@react-icons/all-files/si/SiNodeDotJs";
import { SiNpm } from "@react-icons/all-files/si/SiNpm";
import { SiPhp } from "@react-icons/all-files/si/SiPhp";
import { SiPostman } from "@react-icons/all-files/si/SiPostman";
import { SiPrettier } from "@react-icons/all-files/si/SiPrettier";
import { SiReact } from "@react-icons/all-files/si/SiReact";
import { SiRuby } from "@react-icons/all-files/si/SiRuby";
import { SiShopify } from "@react-icons/all-files/si/SiShopify";
import { SiSketch } from "@react-icons/all-files/si/SiSketch";
import { SiTailwindcss } from "@react-icons/all-files/si/SiTailwindcss";
import { SiTypescript } from "@react-icons/all-files/si/SiTypescript";
import { SiWebpack } from "@react-icons/all-files/si/SiWebpack";
import { SiWordpress } from "@react-icons/all-files/si/SiWordpress";
import { SiYarn } from "@react-icons/all-files/si/SiYarn";

import { SiKubernetes } from "@react-icons/all-files/si/SiKubernetes";
import { SiMicrosoftazure } from "@react-icons/all-files/si/SiMicrosoftazure";
import { SiPython } from "@react-icons/all-files/si/SiPython";
import { SiScala } from "@react-icons/all-files/si/SiScala";
import { SiC } from "@react-icons/all-files/si/SiC";
import { SiGo } from "@react-icons/all-files/si/SiGo";
import { SiCompilerexplorer } from "react-icons/si";
import { SiJulia } from "react-icons/si";
import { SiDocker } from "@react-icons/all-files/si/SiDocker";
import { SiAnsible } from "@react-icons/all-files/si/SiAnsible";
import { SiTerraform } from "@react-icons/all-files/si/SiTerraform";
import { SiR } from "@react-icons/all-files/si/SiR";
import { SiApacheairflow } from "@react-icons/all-files/si/SiApacheairflow";
import { SiApachekafka } from "@react-icons/all-files/si/SiApachekafka";
import { SiVim } from "@react-icons/all-files/si/SiVim";
import { SiNeovim } from "@react-icons/all-files/si/SiNeovim";
import { SiVisualstudio } from "@react-icons/all-files/si/SiVisualstudio";
import { SiLinux } from "@react-icons/all-files/si/SiLinux";
import { SiMongodb } from "@react-icons/all-files/si/SiMongodb";
import { SiPostgresql } from "@react-icons/all-files/si/SiPostgresql";
import { SiRedis } from "@react-icons/all-files/si/SiRedis";
import { SiGooglecloud } from "@react-icons/all-files/si/SiGooglecloud";
import { SiAmazonaws } from "@react-icons/all-files/si/SiAmazonaws";
import { SiAzuredevops } from "@react-icons/all-files/si/SiAzuredevops";
import { SiJenkins } from "@react-icons/all-files/si/SiJenkins";
import { SiGithubactions } from "@react-icons/all-files/si/SiGithubactions";
import { SiGitlab } from "@react-icons/all-files/si/SiGitlab";
import { SiVisualstudiocode } from "@react-icons/all-files/si/SiVisualstudiocode";
import { SiCplusplus } from "@react-icons/all-files/si/SiCplusplus";
import { SiAnaconda } from "@react-icons/all-files/si/SiAnaconda";

/* ------------------------------ SVG Imports ------------------------------ */
/* (Typed strongly as functional components) */

import AWSAmplify from "public/icons/tech-logos/aws_amplify.svg";
import ZIO from "public/icons/tech-logos/zio.svg";
import AWSLambda from "public/icons/tech-logos/aws_lambda.svg";
import Axios from "public/icons/tech-logos/axios.svg";
import Emmet from "public/icons/tech-logos/emmet.svg";
import HeadlessUi from "public/icons/tech-logos/headlessui.svg";
import Jsdom from "public/icons/tech-logos/jsdom.svg";
import Mdx from "public/icons/tech-logos/mdx.svg";
import PlanetScale from "public/icons/tech-logos/planetscale.svg";
import Preact from "public/icons/tech-logos/preact.svg";
import Prisma from "public/icons/tech-logos/prisma.svg";
import ReactQuery from "public/icons/tech-logos/react_query.svg";
import Remix from "public/icons/tech-logos/remix.svg";
import Sendgrid from "public/icons/tech-logos/sendgrid.svg";
import Stylelint from "public/icons/tech-logos/stylelint.svg";
import Swc from "public/icons/tech-logos/swc.svg";
import Takealot from "public/icons/tech-logos/takealot.svg";
import Trpc from "public/icons/tech-logos/trpc.svg";
import Turborepo from "public/icons/tech-logos/turborepo.svg";
import Vend from "public/icons/tech-logos/vend_pos.svg";
import NextJs from "public/icons/tech-logos/nextjs.svg";
import Express from "public/icons/tech-logos/express.svg";
import Liquid from "public/icons/tech-logos/liquid.svg";

/* -------------------------------------------------------------------------- */
/*                                   TECH                                     */
/* -------------------------------------------------------------------------- */
export const TECH_MAP = {
  shopify: { name: "Shopify", Icon: wrap(SiShopify) },
  shopify_polaris: { name: "Shopify Polaris", Icon: wrap(SiShopify) },
  vend: { name: "Vend POS", Icon: wrap(Vend) },

  typescript: { name: "TypeScript", Icon: wrap(SiTypescript) },
  kubernetes: { name: "Kubernetes", Icon: wrap(SiKubernetes) },
  anaconda: { name: "Anaconda", Icon: wrap(SiAnaconda) },
  azure: { name: "Azure", Icon: wrap(SiMicrosoftazure) },
  python: { name: "Python", Icon: wrap(SiPython) },
  scala: { name: "Scala", Icon: wrap(SiScala) },
  c: { name: "C", Icon: wrap(SiC) },
  zio: { name: "Zio", Icon: wrap(ZIO) },
  mainframecobol: {
    name: "COBOL",
    Icon: wrap(SiCompilerexplorer),
  },
  docker: { name: "Docker", Icon: wrap(SiDocker) },
  ansible: { name: "Ansible", Icon: wrap(SiAnsible) },
  terraform: { name: "Terraform", Icon: wrap(SiTerraform) },
  rLang: { name: "R", Icon: wrap(SiR) },
  rust: { name: "Rust", Icon: wrap(SiRust) },
  goLang: { name: "Go", Icon: wrap(SiGo) },
  Julia: { name: "Julia", Icon: wrap(SiJulia) },
  cpp: { name: "CPlusPlus", Icon: wrap(SiCplusplus) },

  apacheAirflow: {
    name: "Apache Airflow",
    Icon: wrap(SiApacheairflow),
  },
  apacheKafka: {
    name: "Apache Kafka",
    Icon: wrap(SiApachekafka),
  },

  vim: { name: "Vim", Icon: wrap(SiVim) },
  nvim: { name: "Neovim", Icon: wrap(SiNeovim) },
  vscode: { name: "VSCode", Icon: wrap(SiVisualstudiocode) },
  vstd: { name: "VisualStudio", Icon: wrap(SiVisualstudio) },
  linux: { name: "Linux", Icon: wrap(SiLinux) },

  mongodb: { name: "MongoDB", Icon: wrap(SiMongodb) },
  postgresql: { name: "PostgreSQL", Icon: wrap(SiPostgresql) },
  redis: { name: "Redis", Icon: wrap(SiRedis) },

  googleCloud: { name: "Google Cloud", Icon: wrap(SiGooglecloud) },
  aws: { name: "Amazon Web Services", Icon: wrap(SiAmazonaws) },
  azureDevOps: { name: "AzureDevOps", Icon: wrap(SiAzuredevops) },
  jenkins: { name: "Jenkins", Icon: wrap(SiJenkins) },
  githubActions: { name: "GitHub Actions", Icon: wrap(SiGithubactions) },
  gitlab: { name: "GitLab", Icon: wrap(SiGitlab) },

  vercel: { name: "Vercel", Icon: wrap(IoLogoVercel) },
  netlify: { name: "Netlify", Icon: wrap(SiNetlify) },
  sass: { name: "Sass", Icon: wrap(IoLogoSass) },
  liquid: { name: "Liquid", Icon: wrap(Liquid) },
  nextjs: { name: "Next.js", Icon: wrap(NextJs) },

  markdown: { name: "Markdown", Icon: wrap(SiMarkdown) },
  mdx: { name: "Mdx", Icon: wrap(Mdx) },
  graphql: { name: "GraphQL", Icon: wrap(SiGraphql) },

  reactjs: { name: "React.js", Icon: wrap(SiReact) },
  tailwind: { name: "TailwindCSS", Icon: wrap(SiTailwindcss) },

  prisma: { name: "Prisma", Icon: wrap(Prisma) },
  planetscale: { name: "PlanetScale", Icon: wrap(PlanetScale) },
  trpc: { name: "Trpc", Icon: wrap(Trpc) },

  amazonwebservices: { name: "AWS", Icon: wrap(FaAws) },
  aws_amplify: { name: "AWS Amplify", Icon: wrap(AWSAmplify) },
  aws_lambda: { name: "AWS Lambda", Icon: wrap(AWSLambda) },

  axios: { name: "Axios", Icon: wrap(Axios) },

  chrome: { name: "Chrome", Icon: wrap(SiGooglechrome) },
  css_3: { name: "CSS", Icon: wrap(SiCss3) },
  emmet: { name: "Emmet", Icon: wrap(Emmet) },
  eslint: { name: "Eslint", Icon: wrap(SiEslint) },

  facebook: { name: "Facebook", Icon: wrap(SiFacebook) },
  figma: { name: "Figma", Icon: wrap(SiFigma) },
  sketch: { name: "Sketch", Icon: wrap(SiSketch) },

  firebase: { name: "Firebase", Icon: wrap(SiFirebase) },
  framer: { name: "Framer", Icon: wrap(SiFramer) },

  git: { name: "Git", Icon: wrap(SiGit) },
  github: { name: "Github", Icon: wrap(SiGithub) },

  headlessui: { name: "Headless Ui", Icon: wrap(HeadlessUi) },

  html_5: { name: "HTML", Icon: wrap(SiHtml5) },

  intellij_idea: { name: "IntellijIdea", Icon: wrap(SiIntellijidea) },
  ionic: { name: "Ionic", Icon: wrap(SiIonic) },

  javascript: { name: "JavaScript", Icon: wrap(SiJavascript) },
  jest: { name: "Jest", Icon: wrap(SiJest) },
  jsdom: { name: "Jsdom", Icon: wrap(Jsdom) },

  mysql: { name: "MySQL", Icon: wrap(SiMysql) },
  nodejs: { name: "Node.js", Icon: wrap(SiNodeDotJs) },

  express: { name: "Express", Icon: wrap(Express) },
  preact: { name: "Preact", Icon: wrap(Preact) },

  prettier: { name: "Prettier", Icon: wrap(SiPrettier) },

  react: { name: "React.js", Icon: wrap(SiReact) },

  dev_tools: { name: "Dev Tools", Icon: wrap(MdDesktopMac) },
  office: { name: "MS Office", Icon: wrap(SiMicrosoftoffice) },

  react_query: { name: "React Query", Icon: wrap(ReactQuery) },
  remix: { name: "Remix", Icon: wrap(Remix) },

  sendgrid: { name: "SendGrid", Icon: wrap(Sendgrid) },
  stylelint: { name: "Stylelint", Icon: wrap(Stylelint) },
  swc: { name: "SWC", Icon: wrap(Swc) },

  tailwindcss: { name: "Tailwindcss", Icon: wrap(SiTailwindcss) },

  takealot: { name: "Takealot", Icon: wrap(Takealot) },
  turborepo: { name: "Turborepo", Icon: wrap(Turborepo) },

  webpack: { name: "Webpack", Icon: wrap(SiWebpack) },
  yarn: { name: "Yarn", Icon: wrap(SiYarn) },
  npm: { name: "NPM", Icon: wrap(SiNpm) },

  sql: { name: "SQL", Icon: wrap(AiOutlineConsoleSql) },
  ruby: { name: "Ruby", Icon: wrap(SiRuby) },
  php: { name: "PHP", Icon: wrap(SiPhp) },

  illustrator: { name: "Illustrator", Icon: wrap(SiAdobeillustrator) },
  photoshop: { name: "Photoshop", Icon: wrap(SiAdobephotoshop) },

  postman: { name: "Postman", Icon: wrap(SiPostman) },

  erply: { name: "Erply POS", Icon: null },
  dear: { name: "Dear POS", Icon: null },
  micros: { name: "Micros POS", Icon: null },

  wordpress: { name: "WordPress", Icon: wrap(SiWordpress) },

  ftp: { name: "Ftp", Icon: wrap(ImUpload) },
  meta: {
    name: "Meta Ads & Facebook Business suite",
    Icon: wrap(ImUpload),
  },
  google: {
    name: "Google Ads, Merchant & Analytics",
    Icon: wrap(ImUpload),
  },

  instagram: { name: "Instagram", Icon: wrap(SiInstagram) },

  omnisend: {
    name: "Omnisend Email Marketing",
    Icon: wrap(SiInstagram),
  },
  asana: { name: "Asana", Icon: wrap(SiInstagram) },
  clockify: { name: "Clockify", Icon: wrap(SiInstagram) },

  msTeams: { name: "Microsoft Teams", Icon: wrap(SiMicrosoftteams) },
  zoom: { name: "Zoom", Icon: wrap(SiInstagram) },
  slack: { name: "Slack", Icon: wrap(SiInstagram) },
} as const;

export const TECH: TechRegistry = TECH_MAP;